/**
 * mitelData.js — Registro ufficiale delle banconote MITEL.
 *
 * FONTE UFFICIALE: documento "Assegnazione ufficiale delle banconote MITEL".
 *
 * Regole:
 *  - Le 11 banconote sono l'ossatura fissa del sistema.
 *  - `available: true` per tutte (rilascio completo).
 *  - `widthMm`/`heightMm` conservano le dimensioni FISICHE ufficiali
 *    (informative). NON governano la dimensione visiva nel Centro Guida:
 *    la UI uniforma tutte le banconote all'ingombro della 5 MITEL.
 *
 * Struttura testuale di ogni banconota:
 *  - principle:       "IL TEMPO SI MISURA" (principio narrativo)
 *  - character:       { name, focus, bio }
 *  - opera:           { name, location, bio }
 *  - symbolic:        relazione simbolica tra volto e opera
 *  - meaning:         significato del taglio temporale
 *  - closing:         frase finale della banconota
 */

export const MITEL_BANKNOTES = [
  {
    id: "5",
    value: 5,
    label: "5 MITEL",
    equivalent: "5 minuti",
    widthMm: 120,
    heightMm: 62,
    principle: "IL TEMPO SI MISURA",
    character: {
      name: "Christiaan Huygens",
      focus: "MISURARE",
      bio: "Huygens trasformò il Tempo in qualcosa che l'uomo poteva misurare con una precisione mai raggiunta prima, applicando il pendolo all'orologio.",
    },
    opera: {
      name: "Piramidi di Giza",
      location: "Egitto",
      bio: "Le Piramidi di Giza sembrano sfidare quella stessa misura: attraversano millenni e continuano a lasciare la loro impronta nel presente.",
    },
    symbolic:
      "Due estremi dello stesso Tempo: l'uomo che cercò di misurarlo e l'opera dell'uomo che sembra attraversarlo.",
    meaning:
      "5 MITEL sono 5 minuti di Tempo libero. Possono sembrare niente. Ma ogni cosa destinata a durare comincia da un istante a cui qualcuno decide di dare valore.",
    closing: "5 MITEL — Misura il Tempo. Poi lascia il Segno.",
    image: "/mitel/mitel-5.webp",
    alt: "Banconota 5 MITEL con Christiaan Huygens e le Piramidi di Giza",
    available: true,
  },
  {
    id: "10",
    value: 10,
    label: "10 MITEL",
    equivalent: "10 minuti",
    widthMm: 122,
    heightMm: 64,
    principle: "IL TEMPO CAMBIA",
    character: {
      name: "Karl Marx",
      focus: "CAMBIARE",
      bio: "Marx osservò la società come qualcosa in continua trasformazione, segnata dal conflitto, dal lavoro e dal cambiamento.",
    },
    opera: {
      name: "Colosseo",
      location: "Roma, Italia",
      bio: "Il Colosseo è ciò che rimane di una civiltà che sembrava eterna e che il Tempo ha trasformato in memoria.",
    },
    symbolic:
      "Due immagini dello stesso principio: nulla resta immobile, nemmeno ciò che sembra destinato a durare per sempre.",
    meaning:
      "10 MITEL sono 10 minuti di Tempo libero. Abbastanza per cambiare una decisione. A volte, anche una direzione.",
    closing: "10 MITEL — Il Tempo cambia tutto.",
    image: "/mitel/mitel-10.webp",
    alt: "Banconota 10 MITEL con Karl Marx e il Colosseo",
    available: true,
  },
  {
    id: "20",
    value: 20,
    label: "20 MITEL",
    equivalent: "20 minuti",
    widthMm: 124,
    heightMm: 66,
    principle: "IL TEMPO CREA",
    character: {
      name: "Michelangelo Buonarroti",
      focus: "CREARE",
      bio: "Michelangelo trasformò materia grezza in opere capaci di attraversare i secoli.",
    },
    opera: {
      name: "Grande Muraglia Cinese",
      location: "Cina",
      bio: "La Grande Muraglia racconta la stessa capacità umana su una scala immensa: costruire oggi qualcosa che continuerà a parlare domani.",
    },
    symbolic:
      "Due manifestazioni della stessa forza: l'uomo crea nel proprio Tempo, ma alcune creazioni riescono a superarlo.",
    meaning:
      "20 MITEL sono 20 minuti di Tempo libero. Non servono secoli per iniziare qualcosa destinato a durare. Serve iniziare.",
    closing: "20 MITEL — Crea qualcosa che resti.",
    image: "/mitel/mitel-20.webp",
    alt: "Banconota 20 MITEL con Michelangelo Buonarroti e la Grande Muraglia Cinese",
    available: true,
  },
  {
    id: "30",
    value: 30,
    label: "30 MITEL",
    equivalent: "30 minuti",
    widthMm: 126,
    heightMm: 68,
    principle: "IL TEMPO CONQUISTA",
    character: {
      name: "Niccolò Machiavelli",
      focus: "CONQUISTARE",
      bio: "Machiavelli studiò il potere, le decisioni e ciò che permette all'uomo di conquistare e conservare ciò che desidera.",
    },
    opera: {
      name: "Taj Mahal",
      location: "Agra, India",
      bio: "Il Taj Mahal racconta un'altra conquista: quella di un sentimento che ha trovato nella pietra il modo di sopravvivere al proprio Tempo.",
    },
    symbolic:
      "Potere e memoria: due modi completamente diversi con cui l'uomo tenta di opporsi a ciò che passa.",
    meaning:
      "30 MITEL sono 30 minuti di Tempo libero. Mezz'ora può essere dispersa senza accorgersene oppure trasformata in qualcosa che conta.",
    closing: "30 MITEL — Conquista il tuo Tempo.",
    image: "/mitel/mitel-30.webp",
    alt: "Banconota 30 MITEL con Niccolò Machiavelli e il Taj Mahal",
    available: true,
  },
  {
    id: "40",
    value: 40,
    label: "40 MITEL",
    equivalent: "40 minuti",
    widthMm: 128,
    heightMm: 70,
    principle: "IL TEMPO SCOPRE",
    character: {
      name: "Cristoforo Colombo",
      focus: "SCOPRIRE",
      bio: "Colombo rappresenta l'impulso umano verso ciò che ancora non conosciamo: partire, cercare, attraversare l'ignoto.",
    },
    opera: {
      name: "Machu Picchu",
      location: "Perù",
      bio: "Machu Picchu, rimasta nascosta agli occhi del mondo per secoli, rappresenta ciò che il Tempo può custodire fino al momento della sua riscoperta.",
    },
    symbolic:
      "Partire e scoprire: perché alcune cose aspettano soltanto qualcuno disposto ad andare oltre ciò che già conosce.",
    meaning:
      "40 MITEL sono 40 minuti di Tempo libero. Puoi percorrere sempre la stessa strada. Oppure usarli per scoprirne una nuova.",
    closing: "40 MITEL — Vai oltre.",
    image: "/mitel/mitel-40.webp",
    alt: "Banconota 40 MITEL con Cristoforo Colombo e Machu Picchu",
    available: true,
  },
  {
    id: "50",
    value: 50,
    label: "50 MITEL",
    equivalent: "50 minuti",
    widthMm: 130,
    heightMm: 72,
    principle: "IL TEMPO COSTRUISCE",
    character: {
      name: "Giulio Cesare",
      focus: "COSTRUIRE",
      bio: "Cesare lasciò un'impronta così profonda nella storia che il suo nome attraversò epoche, popoli e imperi.",
    },
    opera: {
      name: "Basilica di San Pietro",
      location: "Città del Vaticano",
      bio: "La Basilica di San Pietro rappresenta la capacità dell'uomo di costruire qualcosa che diventa più grande della generazione che l'ha iniziata.",
    },
    symbolic:
      "L'eredità e l'opera: ciò che facciamo nel nostro Tempo può continuare quando il nostro Tempo è terminato.",
    meaning:
      "50 MITEL sono 50 minuti di Tempo libero. Quasi un'ora della tua vita. La domanda non è quanto valgano. È cosa puoi costruirci.",
    closing: "50 MITEL — Costruisci ciò che resterà.",
    image: "/mitel/mitel-50.webp",
    alt: "Banconota 50 MITEL con Giulio Cesare e la Basilica di San Pietro",
    available: true,
  },
  {
    id: "60",
    value: 60,
    label: "60 MITEL",
    equivalent: "1 ora",
    widthMm: 132,
    heightMm: 74,
    principle: "IL TEMPO ATTRAVERSA",
    character: {
      name: "Augusto",
      focus: "ATTRAVERSARE",
      bio: "Augusto trasformò Roma e consegnò il proprio nome alla storia.",
    },
    opera: {
      name: "Notre-Dame de Paris",
      location: "Parigi, Francia",
      bio: "Notre-Dame ha attraversato secoli, rivoluzioni, guerre, trasformazioni e perfino il fuoco, continuando a esistere oltre gli uomini che l'hanno costruita.",
    },
    symbolic: "L'uomo passa. L'impronta può restare.",
    meaning:
      "60 MITEL sono 60 minuti di Tempo libero: un'ora. Una delle unità con cui scandiamo la nostra vita. Ogni giorno decidiamo a cosa consegnarla.",
    closing: "60 MITEL — Fai attraversare il Tempo a ciò che crei.",
    image: "/mitel/mitel-60.webp",
    alt: "Banconota 60 MITEL con Augusto e Notre-Dame de Paris",
    available: true,
  },
  {
    id: "1440",
    value: 1440,
    label: "1.440 MITEL",
    equivalent: "1 DAY",
    widthMm: 134,
    heightMm: 76,
    principle: "IL TEMPO OSSERVA",
    character: {
      name: "Galileo Galilei",
      focus: "OSSERVARE",
      bio: "Galileo alzò gli occhi verso il cielo e cambiò il modo in cui l'uomo osservava l'universo.",
    },
    opera: {
      name: "Angkor Wat",
      location: "Cambogia",
      bio: "Angkor Wat attraversa i secoli come testimonianza di una civiltà che osservava il cielo, la natura e il trascorrere del Tempo attraverso la propria architettura.",
    },
    symbolic:
      "Osservare significa comprendere: il cielo sopra di noi, il mondo intorno a noi e il Tempo che ci è concesso.",
    meaning:
      "1.440 MITEL sono un giorno di Tempo libero. Ogni giorno contiene la stessa quantità di minuti. Nessuno può conservarli per domani.",
    closing: "1.440 MITEL — Guarda cosa fai del tuo giorno.",
    image: "/mitel/mitel-1440.webp",
    alt: "Banconota 1.440 MITEL, 1 DAY, con Galileo Galilei e Angkor Wat",
    tier: "vissuto",
    tierLabel: "Tempo vissuto",
    available: true,
  },
  {
    id: "10080",
    value: 10080,
    label: "10.080 MITEL",
    equivalent: "1 WEEK",
    widthMm: 134,
    heightMm: 78,
    principle: "IL TEMPO VIVE",
    character: {
      name: "San Francesco d'Assisi",
      focus: "VIVERE",
      bio: "San Francesco scelse una vita essenziale, profondamente legata alla natura e al ritmo autentico dell'esistenza.",
    },
    opera: {
      name: "Stonehenge",
      location: "Inghilterra",
      bio: "Stonehenge attraversa millenni come testimonianza dell'antico bisogno dell'uomo di osservare il cielo, riconoscere i cicli e orientare la propria vita nel Tempo.",
    },
    symbolic:
      "Due modi di ricordare la stessa cosa: prima degli orologi e dei calendari, il Tempo era qualcosa da osservare, rispettare e vivere.",
    meaning:
      "10.080 MITEL sono una settimana di Tempo libero. Sette giorni possono sembrare tanti quando iniziano. Ma nessuno di quei minuti può essere vissuto una seconda volta.",
    closing: "10.080 MITEL — Non attraversare la settimana. Vivila.",
    image: "/mitel/mitel-10080.webp",
    alt: "Banconota 10.080 MITEL, 1 WEEK, con San Francesco d'Assisi e Stonehenge",
    available: true,
  },
  {
    id: "43800",
    value: 43800,
    label: "43.800 MITEL",
    equivalent: "1 MONTH",
    widthMm: 134,
    heightMm: 80,
    principle: "IL TEMPO INVENTA",
    character: {
      name: "Leonardo da Vinci",
      focus: "INVENTARE",
      bio: "Leonardo immaginò macchine, forme e possibilità che spesso appartenevano a un futuro che lui non avrebbe visto.",
    },
    opera: {
      name: "Torre Eiffel",
      location: "Parigi, Francia",
      bio: "La Torre Eiffel nacque secoli dopo come dimostrazione di ciò che ingegno, tecnica e visione possono rendere reale.",
    },
    symbolic:
      "Immaginare e costruire: prima che qualcosa esista nel mondo, deve esistere nella mente di qualcuno.",
    meaning:
      "43.800 MITEL rappresentano un mese medio di Tempo libero. Il mese MITEL corrisponde a un dodicesimo dell'anno, così che dodici mesi costituiscano esattamente 525.600 MITEL. Un mese è abbastanza lungo per trasformare un'idea in qualcosa che prima non esisteva.",
    closing: "43.800 MITEL — Immagina il futuro. Poi costruiscilo.",
    image: "/mitel/mitel-43800.webp",
    alt: "Banconota 43.800 MITEL, 1 MONTH, con Leonardo da Vinci e la Torre Eiffel",
    available: true,
  },
  {
    id: "525600",
    value: 525600,
    label: "525.600 MITEL",
    equivalent: "1 YEAR",
    widthMm: 134,
    heightMm: 82,
    principle: "IL TEMPO RESTA",
    character: {
      name: "Dante Alighieri",
      focus: "RESTARE",
      bio: "Dante trasformò parole scritte secoli fa in un'opera che continua a parlare agli uomini del presente.",
    },
    opera: {
      name: "Statua della Libertà",
      location: "New York, USA",
      bio: "La Statua della Libertà è diventata un simbolo capace di attraversare generazioni, confini e culture.",
    },
    symbolic:
      "Entrambi ricordano una cosa fondamentale: lasciare il Segno significa creare qualcosa che continui a esistere nel Tempo degli altri.",
    meaning:
      "525.600 MITEL rappresentano un anno di Tempo libero. Dodici mesi. Trecentosessantacinque giorni. Cinquecentoventicinquemilaseicento minuti. Minuti che, una volta trascorsi, non tornano. Alla fine dell'anno non conta soltanto quanto Tempo hai avuto. Conta cosa ne è rimasto.",
    closing: "525.600 MITEL — Un anno per lasciare il Segno.",
    image: "/mitel/mitel-525600.webp",
    alt: "Banconota 525.600 MITEL, 1 YEAR, con Dante Alighieri e la Statua della Libertà",
    tier: "narrativo",
    tierLabel: "Tempo narrativo",
    available: true,
  },
];

/** Formatta le dimensioni FISICHE ufficiali per la scheda banconota. */
export function formatDimensions(b) {
  return `${b.widthMm} × ${b.heightMm} mm`;
}
